import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Button, Badge, Form, Spinner, Alert, Table } from 'react-bootstrap';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../services/api';

const CreditPurchase = () => {
  const { currentUser } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [packages, setPackages] = useState([]);
  const [creditHistory, setCreditHistory] = useState([]);
  const [currentBalance, setCurrentBalance] = useState(0);
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [showPaymentForm, setShowPaymentForm] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [purchaseSuccess, setPurchaseSuccess] = useState(false);

  // Payment form state
  const [paymentForm, setPaymentForm] = useState({
    credit_card_number: '',
    first_name: '',
    last_name: '',
    expiration_month: '',
    expiration_year: '',
    security_code: '',
    address: '',
    address_2: '',
    city: '',
    region: '',
    country: '',
    zip_code: '',
    phone_number: ''
  });

  // Function to fetch data from the API
  const fetchData = async () => {
    try {
      setLoading(true);
      // Fetch packages
      const packagesResponse = await api.get('/billing/packages');

      // Fetch credit history
      const creditsResponse = await api.get('/credits');

      // Use the actual API responses
      if (packagesResponse.data && packagesResponse.data.data && packagesResponse.data.data.packages) {
        setPackages(packagesResponse.data.data.packages);
      }

      if (creditsResponse.data && creditsResponse.data.data) {
        const creditHistory = creditsResponse.data.data;
        setCreditHistory(creditHistory);

        // Set current balance from the most recent transaction's running_balance
        // or 0 if there are no transactions
        if (creditHistory.length > 0) {
          setCurrentBalance(creditHistory[0].running_balance);
        } else {
          setCurrentBalance(0);
        }
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      setError('Failed to load data. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  // Load data on component mount
  useEffect(() => {
    fetchData();
  }, []);

  const handlePackageSelect = (pkg) => {
    setSelectedPackage(pkg);
    setShowPaymentForm(true);

    // Scroll to payment form
    setTimeout(() => {
      document.getElementById('payment-form').scrollIntoView({ behavior: 'smooth' });
    }, 100);
  };

  const handlePaymentInputChange = (e) => {
    const { name, value } = e.target;
    setPaymentForm(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmitPayment = async (e) => {
    e.preventDefault();

    if (!selectedPackage) {
      setError('Please select a credit package.');
      return;
    }

    setProcessing(true);
    setError(null);

    try {
      // Create form data for the API call
      const formData = new FormData();
      formData.append('package_id', selectedPackage.id);
      formData.append('credit_card_number', paymentForm.credit_card_number);
      formData.append('first_name', paymentForm.first_name);
      formData.append('last_name', paymentForm.last_name);
      formData.append('expiration_month', paymentForm.expiration_month);
      formData.append('expiration_year', paymentForm.expiration_year);
      formData.append('security_code', paymentForm.security_code);
      formData.append('address', paymentForm.address);
      if (paymentForm.address_2) formData.append('address_2', paymentForm.address_2);
      formData.append('city', paymentForm.city);
      formData.append('region', paymentForm.region);
      formData.append('country', paymentForm.country);
      formData.append('zip_code', paymentForm.zip_code);
      formData.append('phone_number', paymentForm.phone_number);

      // Make the API call to purchase credits
      const response = await api.post('/billing/purchase', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      if (response.data && response.data.message === 'Success') {
        // Fetch updated credit history and packages
        await fetchData();

        setPurchaseSuccess(true);

        // Reset form
        setShowPaymentForm(false);
        setSelectedPackage(null);
        setPaymentForm({
          credit_card_number: '',
          first_name: '',
          last_name: '',
          expiration_month: '',
          expiration_year: '',
          security_code: '',
          address: '',
          address_2: '',
          city: '',
          region: '',
          country: '',
          zip_code: '',
          phone_number: ''
        });

        // Scroll to top to show success message
        window.scrollTo(0, 0);
      } else {
        setError('Failed to process payment. Please try again.');
      }
    } catch (error) {
      console.error('Error processing payment:', error);
      if (error.response && error.response.data && error.response.data.errors) {
        // Display specific error messages from the API
        const errorMessages = Object.values(error.response.data.errors)
          .flat()
          .join(', ');
        setError(`Payment failed: ${errorMessages}`);
      } else {
        setError('Failed to process payment. Please try again.');
      }
    } finally {
      setProcessing(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <Container className="text-center py-5">
        <Spinner animation="border" variant="primary" />
        <p className="mt-3">Loading credit information...</p>
      </Container>
    );
  }

  return (
    <Container className="py-4">
      <h2 className="fw-bold mb-4">Credits</h2>

      {purchaseSuccess && (
        <Alert variant="success" className="mb-4">
          <i className="bi bi-check-circle-fill me-2"></i>
          Your purchase was successful! Your credits have been added to your account.
        </Alert>
      )}

      {error && (
        <Alert variant="danger" className="mb-4">
          {error}
        </Alert>
      )}

      <Row>
        <Col lg={8} className="mb-4">
          <Card className="border-0 shadow-sm mb-4">
            <Card.Body className="p-4">
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h5 className="fw-bold mb-0">Credit Packages</h5>
                <Badge bg="primary" className="px-3 py-2">
                  <i className="bi bi-coin me-1"></i>
                  Current Balance: {currentBalance} Credits
                </Badge>
              </div>

              <p className="text-muted mb-4">
                Credits can be used to unlock conversations, boost your profile, and access premium features.
              </p>

              <Row>
                {packages.map(pkg => (
                  <Col key={pkg.id} md={4} className="mb-3">
                    <Card
                      className={`h-100 ${selectedPackage?.id === pkg.id ? 'border-primary' : 'border'}`}
                      onClick={() => handlePackageSelect(pkg)}
                      style={{ cursor: 'pointer' }}
                    >
                      <Card.Body className="text-center p-3">
                        <h5 className="fw-bold mb-2">{pkg.name}</h5>
                        <h3 className="mb-2">${pkg.price.toFixed(2)}</h3>

                        {pkg.savings && (
                          <Badge bg="success" className="mb-3">
                            Save {pkg.savings}
                          </Badge>
                        )}

                        <p className="mb-3">
                          <i className="bi bi-coin me-1 text-warning"></i>
                          {pkg.credits} Credits
                        </p>

                        <Button
                          variant={selectedPackage?.id === pkg.id ? "primary" : "outline-primary"}
                          className="w-100"
                        >
                          {selectedPackage?.id === pkg.id ? "Selected" : "Select"}
                        </Button>
                      </Card.Body>
                    </Card>
                  </Col>
                ))}
              </Row>
            </Card.Body>
          </Card>

          {showPaymentForm && (
            <Card className="border-0 shadow-sm mb-4" id="payment-form">
              <Card.Body className="p-4">
                <h5 className="fw-bold mb-3">Payment Information</h5>

                <div className="mb-4">
                  <p className="mb-1">
                    <strong>Selected Package:</strong> {selectedPackage?.name}
                  </p>
                  <p className="mb-1">
                    <strong>Price:</strong> ${selectedPackage?.price.toFixed(2)}
                  </p>
                  <p className="mb-0">
                    <strong>Credits:</strong> {selectedPackage?.credits}
                  </p>
                </div>

                <Form onSubmit={handleSubmitPayment}>
                  <h6 className="fw-bold mb-3">Card Details</h6>

                  <Row className="mb-3">
                    <Col md={12} className="mb-3">
                      <Form.Group>
                        <Form.Label>Card Number</Form.Label>
                        <Form.Control
                          type="text"
                          name="credit_card_number"
                          value={paymentForm.credit_card_number}
                          onChange={handlePaymentInputChange}
                          placeholder="1234 5678 9012 3456"
                          required
                        />
                      </Form.Group>
                    </Col>

                    <Col md={6} className="mb-3">
                      <Form.Group>
                        <Form.Label>First Name</Form.Label>
                        <Form.Control
                          type="text"
                          name="first_name"
                          value={paymentForm.first_name}
                          onChange={handlePaymentInputChange}
                          required
                        />
                      </Form.Group>
                    </Col>

                    <Col md={6} className="mb-3">
                      <Form.Group>
                        <Form.Label>Last Name</Form.Label>
                        <Form.Control
                          type="text"
                          name="last_name"
                          value={paymentForm.last_name}
                          onChange={handlePaymentInputChange}
                          required
                        />
                      </Form.Group>
                    </Col>

                    <Col md={4} className="mb-3">
                      <Form.Group>
                        <Form.Label>Expiration Month</Form.Label>
                        <Form.Select
                          name="expiration_month"
                          value={paymentForm.expiration_month}
                          onChange={handlePaymentInputChange}
                          required
                        >
                          <option value="">Month</option>
                          {Array.from({ length: 12 }, (_, i) => (
                            <option key={i + 1} value={i + 1}>
                              {i + 1}
                            </option>
                          ))}
                        </Form.Select>
                      </Form.Group>
                    </Col>

                    <Col md={4} className="mb-3">
                      <Form.Group>
                        <Form.Label>Expiration Year</Form.Label>
                        <Form.Select
                          name="expiration_year"
                          value={paymentForm.expiration_year}
                          onChange={handlePaymentInputChange}
                          required
                        >
                          <option value="">Year</option>
                          {Array.from({ length: 10 }, (_, i) => {
                            const year = new Date().getFullYear() + i;
                            return (
                              <option key={year} value={year}>
                                {year}
                              </option>
                            );
                          })}
                        </Form.Select>
                      </Form.Group>
                    </Col>

                    <Col md={4} className="mb-3">
                      <Form.Group>
                        <Form.Label>Security Code</Form.Label>
                        <Form.Control
                          type="text"
                          name="security_code"
                          value={paymentForm.security_code}
                          onChange={handlePaymentInputChange}
                          placeholder="123"
                          required
                        />
                      </Form.Group>
                    </Col>
                  </Row>

                  <h6 className="fw-bold mb-3">Billing Address</h6>

                  <Row className="mb-3">
                    <Col md={12} className="mb-3">
                      <Form.Group>
                        <Form.Label>Address</Form.Label>
                        <Form.Control
                          type="text"
                          name="address"
                          value={paymentForm.address}
                          onChange={handlePaymentInputChange}
                          required
                        />
                      </Form.Group>
                    </Col>

                    <Col md={12} className="mb-3">
                      <Form.Group>
                        <Form.Label>Address Line 2 (Optional)</Form.Label>
                        <Form.Control
                          type="text"
                          name="address_2"
                          value={paymentForm.address_2}
                          onChange={handlePaymentInputChange}
                        />
                      </Form.Group>
                    </Col>

                    <Col md={6} className="mb-3">
                      <Form.Group>
                        <Form.Label>City</Form.Label>
                        <Form.Control
                          type="text"
                          name="city"
                          value={paymentForm.city}
                          onChange={handlePaymentInputChange}
                          required
                        />
                      </Form.Group>
                    </Col>

                    <Col md={6} className="mb-3">
                      <Form.Group>
                        <Form.Label>State/Province/Region</Form.Label>
                        <Form.Control
                          type="text"
                          name="region"
                          value={paymentForm.region}
                          onChange={handlePaymentInputChange}
                          required
                        />
                      </Form.Group>
                    </Col>

                    <Col md={6} className="mb-3">
                      <Form.Group>
                        <Form.Label>Country</Form.Label>
                        <Form.Control
                          type="text"
                          name="country"
                          value={paymentForm.country}
                          onChange={handlePaymentInputChange}
                          required
                        />
                      </Form.Group>
                    </Col>

                    <Col md={6} className="mb-3">
                      <Form.Group>
                        <Form.Label>Zip/Postal Code</Form.Label>
                        <Form.Control
                          type="text"
                          name="zip_code"
                          value={paymentForm.zip_code}
                          onChange={handlePaymentInputChange}
                          required
                        />
                      </Form.Group>
                    </Col>

                    <Col md={12} className="mb-3">
                      <Form.Group>
                        <Form.Label>Phone Number</Form.Label>
                        <Form.Control
                          type="text"
                          name="phone_number"
                          value={paymentForm.phone_number}
                          onChange={handlePaymentInputChange}
                          required
                        />
                      </Form.Group>
                    </Col>
                  </Row>

                  <div className="d-flex justify-content-between">
                    <Button
                      variant="outline-secondary"
                      onClick={() => setShowPaymentForm(false)}
                      disabled={processing}
                    >
                      Cancel
                    </Button>

                    <Button
                      type="submit"
                      variant="primary"
                      disabled={processing}
                    >
                      {processing ? (
                        <>
                          <Spinner
                            as="span"
                            animation="border"
                            size="sm"
                            role="status"
                            aria-hidden="true"
                            className="me-2"
                          />
                          Processing...
                        </>
                      ) : (
                        `Pay $${selectedPackage?.price.toFixed(2)}`
                      )}
                    </Button>
                  </div>
                </Form>
              </Card.Body>
            </Card>
          )}
        </Col>

        <Col lg={4}>
          <Card className="border-0 shadow-sm mb-4">
            <Card.Body className="p-4">
              <h5 className="fw-bold mb-3">Credit Balance</h5>

              <div className="text-center mb-3">
                <div className="display-4 fw-bold text-primary mb-2">
                  {currentBalance}
                </div>
                <p className="text-muted">Available Credits</p>
              </div>

              <div className="mb-3">
                <h6 className="fw-bold">What You Can Do With Credits:</h6>
                <ul className="mb-0">
                  <li>Unlock conversations (5 credits)</li>
                  <li>Boost your profile (10 credits)</li>
                  <li>Send priority messages (2 credits)</li>
                  <li>Access premium features</li>
                </ul>
              </div>
            </Card.Body>
          </Card>

          <Card className="border-0 shadow-sm">
            <Card.Body className="p-4">
              <h5 className="fw-bold mb-3">Transaction History</h5>

              {creditHistory.length === 0 ? (
                <p className="text-center text-muted">No transactions yet</p>
              ) : (
                <Table responsive className="mb-0">
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Type</th>
                      <th>Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {creditHistory.map(transaction => (
                      <tr key={transaction.id}>
                        <td>{formatDate(transaction.created_dt)}</td>
                        <td>
                          {transaction.transaction_type === 'Purchase' ? (
                            <Badge bg="success">Purchase</Badge>
                          ) : (
                            <Badge bg="secondary">{transaction.transaction_type}</Badge>
                          )}
                        </td>
                        <td className={transaction.amount > 0 ? 'text-success' : 'text-danger'}>
                          {transaction.amount > 0 ? '+' : ''}{transaction.amount}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default CreditPurchase;
